import { useState } from "react";
import { Search, ChevronUp, ChevronDown, List } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { cn } from "@/lib/utils";
import { useIsMobile } from "@/hooks/use-mobile";

export interface TocSection {
  id: string;
  label: string;
  present: boolean;
}

interface ReportNavigatorProps {
  sections: TocSection[];
  activeId: string;
  onNavigate: (id: string) => void;
  onExpandAll: () => void;
  onCollapseAll: () => void;
  scrollProgress: number;
}

const TocList = ({
  sections,
  activeId,
  searchQuery,
  onSearchChange,
  onNavigate,
  onExpandAll,
  onCollapseAll,
  scrollProgress,
}: ReportNavigatorProps & { searchQuery: string; onSearchChange: (q: string) => void }) => {
  const filtered = sections
    .filter((s) => s.present)
    .filter((s) => s.label.toLowerCase().includes(searchQuery.toLowerCase()));

  return (
    <div className="flex flex-col gap-3">
      {/* Search */}
      <div className="relative">
        <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
        <Input
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder="Search sections…"
          className="h-8 pl-8 text-xs bg-white/5 border-white/10 text-slate-200 placeholder:text-slate-400"
        />
      </div>

      {/* Expand / Collapse */}
      <div className="flex gap-1">
        <Button
          variant="ghost"
          size="sm"
          className="h-7 flex-1 text-[11px] text-slate-300 hover:text-white"
          onClick={onExpandAll}
        >
          <ChevronDown className="mr-1 h-3 w-3" /> Expand all
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className="h-7 flex-1 text-[11px] text-slate-300 hover:text-white"
          onClick={onCollapseAll}
        >
          <ChevronUp className="mr-1 h-3 w-3" /> Collapse all
        </Button>
      </div>

      {/* TOC Items */}
      <nav className="flex flex-col gap-0.5">
        {filtered.map((s) => (
          <button
            key={s.id}
            onClick={() => onNavigate(s.id)}
            className={cn(
              "text-left text-xs px-2.5 py-1.5 rounded-lg transition-colors",
              activeId === s.id
                ? "bg-teal-500/15 text-teal-300 font-medium"
                : "text-slate-300 hover:text-slate-100 hover:bg-white/5"
            )}
          >
            {s.label}
          </button>
        ))}
        {filtered.length === 0 && (
          <p className="text-[11px] text-slate-500 px-2.5 py-1">No matching sections</p>
        )}
      </nav>
    </div>
  );
};

const ReportNavigator = (props: ReportNavigatorProps) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [sheetOpen, setSheetOpen] = useState(false);
  const isMobile = useIsMobile();

  const handleNavigate = (id: string) => {
    props.onNavigate(id);
    setSheetOpen(false);
  };

  // Desktop sidebar (LEFT, 280px)
  if (!isMobile) {
    return (
      <aside className="hidden lg:block w-[280px] shrink-0">
        <div className="sticky top-20 space-y-1 max-h-[calc(100vh-6rem)] overflow-y-auto pr-2">
          <div className="flex items-center justify-between mb-3 px-2.5">
            <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-300">
              Contents
            </h4>
            <span className="text-[10px] font-medium text-teal-400">{Math.round(props.scrollProgress)}%</span>
          </div>
          <TocList
            {...props}
            onNavigate={handleNavigate}
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
          />
        </div>
      </aside>
    );
  }

  // Mobile floating button + bottom sheet
  return (
    <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
      <SheetTrigger asChild>
        <Button
          size="icon"
          className="fixed bottom-6 right-6 z-50 h-12 w-12 rounded-full btn-gradient shadow-lg shadow-black/40"
        >
          <List className="h-5 w-5" />
        </Button>
      </SheetTrigger>
      <SheetContent side="bottom" className="bg-[#0a0e1a] border-white/10 max-h-[70vh] overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="text-white text-sm flex items-center justify-between">
            Contents
            <span className="text-[10px] font-medium text-teal-400">{Math.round(props.scrollProgress)}%</span>
          </SheetTitle>
        </SheetHeader>
        <div className="mt-2">
          <TocList
            {...props}
            onNavigate={handleNavigate}
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
          />
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default ReportNavigator;
